import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTerminalsStatus, posLoginByTerminal, forceLogoutTerminal } from '../services/api';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Package,
  Store,
  Receipt,
  BarChart3,
  Settings,
  LogOut,
  Users,
  Ticket,
  HelpCircle,
  Tags,
  FileText,
  ShoppingCart,
  Truck,
  Image,
  Mail,
  Menu,
  X,
  DollarSign,
  Wrench,
  Building2,
  ChevronDown,
  ClipboardList,
  UserCheck,
  Wallet,
  Crown,
  TrendingUp,
  KeyRound,
  QrCode,
  ShoppingBag,
  LayoutGrid,
  Layers,
  RefreshCw,
  List,
  Clock,
  MapPin,
  Radio,
  MessageCircle,
  Shield,
  Bike,
  Monitor,
  AlertTriangle,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { hasPermission, isGodAdmin } from '../utils/permissions';

// ==================== POS TERMINAL SELECTOR MODAL ====================
const POSTerminalModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('select'); // 'select' | 'conflict' | 'pin'
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [pin, setPin] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [error, setError] = useState('');
  const [forceLoading, setForceLoading] = useState(false);
  const pinInputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const getDeviceId = () => {
    let deviceId = localStorage.getItem('pos_device_id');
    if (!deviceId) {
      deviceId = 'DEV-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
      localStorage.setItem('pos_device_id', deviceId);
    }
    return deviceId;
  };

  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedTerminal(null);
      setPin('');
      setError('');
      loadTerminals();
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 'pin') {
      setTimeout(() => pinInputRefs[0].current?.focus(), 150);
    }
  }, [step]);

  const loadTerminals = async () => {
    setLoading(true);
    try {
      const res = await getTerminalsStatus();
      setTerminals(res.data);
    } catch (err) {
      toast.error('Error al cargar las cajas');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTerminal = (terminal) => {
    if (!terminal.is_active) return;
    setSelectedTerminal(terminal);
    setError('');

    if (terminal.active_session_id && terminal.active_session_device !== getDeviceId()) {
      setStep('conflict');
    } else {
      setStep('pin');
    }
  };

  const handleForceLogout = async () => {
    if (!selectedTerminal) return;
    setForceLoading(true);
    setError('');
    try {
      await forceLogoutTerminal(selectedTerminal.id);
      // Refresh terminal data
      const res = await getTerminalsStatus();
      setTerminals(res.data);
      const updated = res.data.find(t => t.id === selectedTerminal.id);
      if (updated) setSelectedTerminal(updated);
      setStep('pin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cerrar sesión remota');
    } finally {
      setForceLoading(false);
    }
  };

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = pin.split('');
    newPin[index] = value;
    const joined = newPin.join('');
    setPin(joined);
    if (value && index < 5) {
      pinInputRefs[index + 1].current?.focus();
    }
    // Auto-submit when 6 digits entered
    if (joined.length === 6 && index === 5) {
      submitPin(joined);
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinInputRefs[index - 1].current?.focus();
    }
  };

  useEffect(() => {
    if (pin.length === 6 && step === 'pin') {
      submitPin(pin);
    }
  }, [pin]);

  const submitPin = async (currentPin) => {
    if (!selectedTerminal || pinLoading) return;
    setPinLoading(true);
    setError('');
    try {
      const response = await posLoginByTerminal(selectedTerminal.id, currentPin, getDeviceId());
      // Save session and open POS in new tab
      const sessionData = response.data;
      // Store session in a temporary key for the new tab to pick up
      localStorage.setItem('pos_session_pending', JSON.stringify(sessionData));
      if (sessionData.terminal?.hash) {
        localStorage.setItem('pos_terminal_hash', sessionData.terminal.hash);
      }
      onClose();
      window.open('/pos?from=dashboard', '_blank');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al iniciar sesión';
      if (err.response?.status === 409) {
        // Session conflict appeared after force-logout race condition
        await loadTerminals();
        const updated = terminals.find(t => t.id === selectedTerminal.id);
        if (updated) setSelectedTerminal(updated);
        setStep('conflict');
        setError('');
      } else {
        setError(detail);
      }
      setPin('');
      setTimeout(() => pinInputRefs[0].current?.focus(), 100);
    } finally {
      setPinLoading(false);
    }
  };

  if (!isOpen) return null;

  // Group terminals by branch
  const grouped = terminals.reduce((acc, t) => {
    const key = t.branch_name || 'Sin sucursal';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            {step !== 'select' && (
              <button
                onClick={() => { setStep('select'); setPin(''); setError(''); }}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {step === 'select' && 'Selecciona una Caja'}
                {step === 'conflict' && 'Sesión Activa'}
                {step === 'pin' && 'Ingresa tu PIN'}
              </h2>
              {step === 'select' && <p className="text-sm text-slate-500 mt-0.5">Elige la caja donde deseas operar</p>}
              {step === 'pin' && selectedTerminal && (
                <p className="text-sm text-slate-500 mt-0.5">{selectedTerminal.name} — {selectedTerminal.branch_name}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* STEP: Select Terminal */}
          {step === 'select' && (
            loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
            ) : terminals.length === 0 ? (
              <div className="text-center py-12">
                <Monitor className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No hay cajas registradas</p>
                <p className="text-sm text-slate-400 mt-1">Ve a Estructura &gt; Cajas para crear una</p>
              </div>
            ) : (
              <div className="space-y-5">
                {Object.entries(grouped).map(([branchName, branchTerminals]) => (
                  <div key={branchName}>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">
                      {branchName}
                    </p>
                    <div className="space-y-2">
                      {branchTerminals.map((terminal) => {
                        const isInUse = !!terminal.active_session_id;
                        const isInactive = !terminal.is_active;
                        return (
                          <button
                            key={terminal.id}
                            onClick={() => handleSelectTerminal(terminal)}
                            disabled={isInactive}
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                              isInactive
                                ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                                : isInUse
                                  ? 'border-amber-200 bg-amber-50 hover:border-amber-400 hover:shadow-md cursor-pointer'
                                  : 'border-slate-200 bg-white hover:border-teal-400 hover:shadow-md cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  isInactive ? 'bg-slate-200' : isInUse ? 'bg-amber-100' : 'bg-teal-50'
                                }`}>
                                  <Monitor className={`w-5 h-5 ${
                                    isInactive ? 'text-slate-400' : isInUse ? 'text-amber-600' : 'text-teal-600'
                                  }`} />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-900">{terminal.name}</p>
                                  {isInactive && (
                                    <p className="text-xs text-red-500 font-medium">Desactivada</p>
                                  )}
                                  {isInUse && !isInactive && (
                                    <p className="text-xs text-amber-600">
                                      En uso por <span className="font-medium">{terminal.session_cashier_name || 'Cajero'}</span>
                                      {terminal.session_started_at && (
                                        <> — desde {new Date(terminal.session_started_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</>
                                      )}
                                    </p>
                                  )}
                                  {!isInUse && !isInactive && (
                                    <p className="text-xs text-green-600 font-medium">Disponible</p>
                                  )}
                                </div>
                              </div>
                              {!isInactive && (
                                <ArrowRight className={`w-4 h-4 ${isInUse ? 'text-amber-400' : 'text-slate-300'}`} />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* STEP: Conflict */}
          {step === 'conflict' && selectedTerminal && (
            <div className="py-4">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-amber-600" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-900 text-center mb-2">
                Sesión activa en otro dispositivo
              </h3>
              <p className="text-slate-500 text-center text-sm mb-2">
                <span className="font-semibold text-slate-700">{selectedTerminal.name}</span> tiene una sesión abierta
                {selectedTerminal.session_cashier_name && (
                  <> por <span className="font-semibold text-slate-700">{selectedTerminal.session_cashier_name}</span></>
                )}
              </p>
              {selectedTerminal.session_started_at && (
                <p className="text-xs text-slate-400 text-center mb-6">
                  Iniciada: {new Date(selectedTerminal.session_started_at).toLocaleString('es-MX')}
                </p>
              )}

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleForceLogout}
                  disabled={forceLoading}
                  className="w-full py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {forceLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <LogOut className="w-4 h-4" />
                      Cerrar sesión remota y abrir aquí
                    </>
                  )}
                </button>
                <button
                  onClick={() => { setStep('select'); setError(''); }}
                  className="w-full py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-all"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* STEP: PIN Entry */}
          {step === 'pin' && (
            <div className="py-4">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center">
                  <KeyRound className="w-8 h-8 text-teal-600" />
                </div>
              </div>
              <p className="text-center text-slate-500 text-sm mb-6">
                Ingresa el PIN de 6 dígitos del cajero
              </p>
              <div className="flex justify-center gap-2 mb-6">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={pinInputRefs[index]}
                    type="password"
                    inputMode="numeric"
                    maxLength={1}
                    value={pin[index] || ''}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                ))}
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              {pinLoading && (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="w-7 h-7 text-teal-600 animate-spin" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPOSModal, setShowPOSModal] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isSuperAdmin = user?.role === 'superadmin';

  const allSuperAdminMenu = [
    { type: 'link', icon: LayoutDashboard, label: 'Dashboard', path: '/superadmin', permission: 'dashboard' },
    {
      type: 'group', key: 'sa_catalogo', icon: Store, label: 'Negocios y Catálogo',
      children: [
        { icon: Store, label: 'Negocios', path: '/superadmin/negocios', permission: 'negocios' },
        { icon: Tags, label: 'Categorías', path: '/superadmin/categorias', permission: 'categorias' },
        { icon: Building2, label: 'Tipos de Negocio', path: '/superadmin/tipos-negocio', permission: 'tipos_negocio' },
        { icon: Layers, label: 'Cat. Maestras', path: '/superadmin/categorias-maestras', permission: 'categorias_maestras' },
      ],
    },
    {
      type: 'group', key: 'sa_apariencia', icon: Image, label: 'Apariencia',
      children: [
        { icon: Image, label: 'Banners', path: '/superadmin/banners', permission: 'banners' },
        { icon: LayoutGrid, label: 'Landing', path: '/superadmin/landing-layout', permission: 'landing' },
      ],
    },
    {
      type: 'group', key: 'sa_licencias', icon: KeyRound, label: 'Licencias y Ventas',
      children: [
        { icon: KeyRound, label: 'Licencias POS', path: '/superadmin/licencias-pos', permission: 'licencias' },
        { icon: DollarSign, label: 'Ventas Licencias', path: '/superadmin/ventas-licencias', permission: 'licencias' },
        { icon: ShoppingBag, label: 'Compras Web', path: '/superadmin/compras', permission: 'compras' },
      ],
    },
    {
      type: 'group', key: 'sa_operacion', icon: Wrench, label: 'Operación',
      children: [
        { icon: Ticket, label: 'Tickets Soporte', path: '/superadmin/tickets', permission: 'tickets' },
        { icon: RefreshCw, label: 'Sync POS', path: '/superadmin/sync-dashboard', permission: 'sync' },
        { icon: Radio, label: 'Delivery Hub', path: '/superadmin/delivery-hub', permission: 'delivery_hub' },
        { icon: Bike, label: 'Repartidores enLocal', path: '/superadmin/gig-drivers', permission: 'gig_drivers' },
        { icon: DollarSign, label: 'Liquidaciones', path: '/superadmin/gig-settlements', permission: 'gig_drivers' },
        { icon: MessageCircle, label: 'WhatsApp', path: '/superadmin/whatsapp', permission: 'whatsapp' },
        { icon: QrCode, label: 'Pass — Retiros', path: '/superadmin/pass/payouts' },
        { icon: Building2, label: 'Pass — Settlements', path: '/superadmin/pass/settlements' },
        { icon: BarChart3, label: 'Pass — Reportes', path: '/superadmin/pass/reportes' },
        { icon: FileText, label: 'Pass — Guías', path: '/superadmin/pass/guias' },
      ],
    },
    {
      type: 'group', key: 'sa_sistema', icon: Settings, label: 'Sistema',
      children: [
        { icon: Mail, label: 'Email', path: '/superadmin/email', permission: 'email' },
        { icon: FileText, label: 'Facturación', path: '/superadmin/facturacion', permission: 'facturacion_admin' },
        { icon: Settings, label: 'Configuración', path: '/superadmin/configuracion', permission: 'configuracion' },
        { icon: Shield, label: 'Usuarios Admin', path: '/superadmin/usuarios-admin', godOnly: true },
      ],
    },
  ];

  // Filter superadmin menu by permissions (remove children without permission, remove empty groups)
  const superAdminMenu = allSuperAdminMenu
    .map(item => {
      if (item.type === 'group') {
        const filteredChildren = item.children.filter(child => {
          if (child.godOnly) return isGodAdmin(user);
          return hasPermission(user, child.permission);
        });
        return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
      }
      return hasPermission(user, item.permission) ? item : null;
    })
    .filter(Boolean);

  const businessMenu = [
    { type: 'link', icon: LayoutDashboard, label: 'Resumen', path: '/dashboard' },
    {
      type: 'group', key: 'ventas', icon: DollarSign, label: 'Ventas',
      children: [
        { icon: ShoppingCart, label: 'Abrir POS', path: '/pos', action: 'openPOS' },
        { icon: Receipt, label: 'Historial Ventas', path: '/dashboard/ventas' },
        { icon: ClipboardList, label: 'Cotizaciones', path: '/dashboard/cotizaciones' },
        { icon: UserCheck, label: 'Clientes', path: '/dashboard/clientes' },
      ],
    },
    { type: 'link', icon: Radio, label: 'Delivery Hub', path: '/dashboard/delivery-hub' },
    {
      type: 'group', key: 'pass', icon: QrCode, label: 'Pass',
      children: [
        { icon: LayoutDashboard, label: 'Resumen', path: '/dashboard/pass' },
        { icon: FileText, label: 'Nueva oferta', path: '/dashboard/pass/ofertas/nueva' },
        { icon: QrCode, label: 'Scanner QR', path: '/dashboard/pass/scanner' },
      ],
    },
    {
      type: 'group', key: 'facturacion', icon: FileText, label: 'Facturación',
      children: [
        { icon: Receipt, label: 'Facturación de Tickets', path: '/dashboard/facturacion' },
        { icon: FileText, label: 'Facturación Manual', path: '/dashboard/facturacion-manual' },
      ],
    },
    {
      type: 'group', key: 'operacion', icon: Package, label: 'Operación',
      children: [
        { icon: Package, label: 'Productos', path: '/dashboard/productos' },
        { icon: Tags, label: 'Secciones', path: '/dashboard/secciones' },
        { icon: List, label: 'Listas de Precios', path: '/dashboard/listas-precios' },
        { icon: Receipt, label: 'Inventario', path: '/dashboard/inventario' },
        { icon: Clock, label: 'Cierres de Turno', path: '/dashboard/cierres-turno' },
        { icon: RefreshCw, label: 'Sync Log', path: '/dashboard/sync-log' },
        { icon: Package, label: 'Ajustes Inventario', path: '/dashboard/ajustes-inventario' },
        { icon: Users, label: 'Personal', path: '/dashboard/cajeros' },
        { icon: Truck, label: 'Reparto', path: '/dashboard/repartidores' },
        { icon: MapPin, label: 'Seguimiento', path: '/dashboard/seguimiento-entregas' },
        { icon: FileText, label: 'Entregas Remisiones', path: '/dashboard/entregas-remisiones' },
      ],
    },
    {
      type: 'group', key: 'estructura', icon: Building2, label: 'Estructura',
      children: [
        { icon: Store, label: 'Sucursales', path: '/dashboard/sucursales' },
        { icon: Receipt, label: 'Cajas', path: '/dashboard/cajas' },
      ],
    },
    {
      type: 'group', key: 'reportes', icon: BarChart3, label: 'Reportes',
      children: [
        { icon: TrendingUp, label: 'Analisis Ventas', path: '/dashboard/analisis-ventas' },
        { icon: DollarSign, label: 'Ventas', path: '/dashboard/reportes/ventas' },
        { icon: Package, label: 'Inventario', path: '/dashboard/reportes/inventario' },
        { icon: Wallet, label: 'Caja', path: '/dashboard/reportes/caja' },
        { icon: TrendingUp, label: 'Productos Top', path: '/dashboard/reportes/top-productos' },
        { icon: ShoppingCart, label: 'Marketplace', path: '/dashboard/reportes/marketplace' },
        { icon: ClipboardList, label: 'Cotizaciones', path: '/dashboard/reportes/cotizaciones' },
      ],
    },
    {
      type: 'group', key: 'sistema', icon: Wrench, label: 'Sistema',
      children: [
        { icon: Settings, label: 'Configuración', path: '/dashboard/configuracion' },
        { icon: QrCode, label: 'Menú Digital', path: '/dashboard/menu-digital' },
        { icon: KeyRound, label: 'Licencias POS', path: '/dashboard/licencias-pos' },
        { icon: Crown, label: 'Plan y Complementos', path: '/dashboard/plan' },
        { icon: ShoppingBag, label: 'Comprar Addons', path: '/dashboard/comprar-addons' },
        { icon: HelpCircle, label: 'Soporte', path: '/dashboard/soporte' },
      ],
    },
  ];

  const activeMenu = isSuperAdmin ? superAdminMenu : businessMenu;

  // Determine which groups should be open based on current route
  const getInitialOpenGroups = () => {
    const open = [];
    activeMenu.forEach((item) => {
      if (item.type === 'group') {
        const hasActive = item.children.some(
          (child) => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
        );
        if (hasActive) open.push(item.key);
      }
    });
    return open;
  };

  const [openGroups, setOpenGroups] = useState(getInitialOpenGroups);

  // Update open groups when route changes
  useEffect(() => {
    activeMenu.forEach((item) => {
      if (item.type === 'group') {
        const hasActive = item.children.some(
          (child) => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
        );
        if (hasActive) {
          setOpenGroups((prev) => (prev.includes(item.key) ? prev : [...prev, item.key]));
        }
      }
    });
  }, [location.pathname]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const sidebarContent = (
    <>
      <div className="p-4 md:p-6 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Todo en Local</h1>
          <p className="text-sm text-slate-400 mt-1">{isSuperAdmin ? 'SuperAdmin' : user?.full_name}</p>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden p-1 rounded-lg hover:bg-slate-800 text-slate-400"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 p-2 md:p-4 space-y-0.5 md:space-y-1 overflow-y-auto">
        {activeMenu.map((item) => {
          if (item.type === 'link') {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 md:px-4 md:py-3 rounded-lg transition-colors text-sm md:text-base ${
                  isActive
                    ? 'bg-teal-700 text-white'
                    : 'hover:bg-slate-800 text-slate-300'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          }

          const Icon = item.icon;
          const isOpen = openGroups.includes(item.key);
          const hasActiveChild = item.children.some(
            (child) => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
          );

          return (
            <div key={item.key}>
              <button
                onClick={() => toggleGroup(item.key)}
                className={`flex items-center gap-3 px-3 py-2.5 md:px-4 md:py-3 w-full rounded-lg transition-colors text-sm md:text-base ${
                  hasActiveChild
                    ? 'text-white'
                    : 'hover:bg-slate-800 text-slate-300'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium flex-1 text-left">{item.label}</span>
                <ChevronDown
                  className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  isOpen ? 'max-h-[40rem] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="ml-2 pl-4 border-l border-slate-700 space-y-0.5 py-1">
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const isChildActive = location.pathname === child.path || location.pathname.startsWith(child.path + '/');

                    if (child.action === 'openPOS') {
                      return (
                        <button
                          key={child.path}
                          onClick={() => setShowPOSModal(true)}
                          data-testid={`nav-${child.label.toLowerCase()}`}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm hover:bg-slate-800 text-slate-400 w-full text-left"
                        >
                          <ChildIcon className="w-4 h-4 flex-shrink-0" />
                          <span>{child.label}</span>
                        </button>
                      );
                    }

                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        data-testid={`nav-${child.label.toLowerCase()}`}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                          isChildActive
                            ? 'bg-teal-700 text-white'
                            : 'hover:bg-slate-800 text-slate-400'
                        }`}
                      >
                        <ChildIcon className="w-4 h-4 flex-shrink-0" />
                        <span>{child.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      {!isSuperAdmin && (
        <div className="p-3 md:p-4 border-t border-slate-800">
          <button
            onClick={() => setShowPOSModal(true)}
            data-testid="nav-pos-btn"
            className="flex items-center justify-center gap-2 px-4 py-2.5 md:py-3 w-full bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-lg hover:from-teal-600 hover:to-emerald-700 transition-all shadow-lg shadow-teal-500/25 text-sm md:text-base"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Abrir POS</span>
          </button>
        </div>
      )}

      <div className="p-3 md:p-4 border-t border-slate-800">
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-3 px-3 py-2.5 md:px-4 md:py-3 w-full rounded-lg hover:bg-slate-800 text-slate-300 transition-colors text-sm md:text-base"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Cerrar Sesión</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900 text-white flex items-center px-4 h-14 shadow-lg">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-1.5 rounded-lg hover:bg-slate-800"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="ml-3 text-lg font-bold">Todo en Local</h1>
      </div>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - desktop: static, mobile: overlay drawer */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-50
          w-64 bg-slate-900 text-slate-300 flex flex-col
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:flex-shrink-0
        `}
      >
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 md:pt-0">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* POS Terminal Selector Modal */}
      <POSTerminalModal isOpen={showPOSModal} onClose={() => setShowPOSModal(false)} />
    </div>
  );
};